import * as React from "react";
import SurveyForm from "./controls/SurveyForm";
import { QuestionType, SocialData } from "../../types";
import StoreContainer from "../../stores/StoreContainer";
import isSectionVisible from "./helpers/isSectionVisible";

interface SurveyProps {
  survey: any;
  onSubmit: any;
  submitButtonText: any;
  agentRating: number;
  dynamicProperties: any;
  visitorName: string;
  visitorEmail: string;
  socialData: SocialData;
  headerText: string;
}

interface SurveyState {
  answers: any;
  visibleSections: any;
  currentPage: number;
  submittingSurvey: boolean;
  totalPages: any;
  errors: any;
  pagedQuestions: any;
}

class Survey extends React.Component<SurveyProps, SurveyState> {
  sectionParents: any;
  sectionHash: any;
  el: any;

  constructor(props: SurveyProps, state: SurveyState) {
    super(props, state);
    this.state = this.loadState();
  }

  UNSAFE_componentWillReceiveProps(nextProps: SurveyProps) {
    const answers = { ...this.state.answers };

    if (this.props.socialData !== nextProps.socialData) {
      nextProps.survey.SurveyQuestions.forEach((question: any) => {
        this.setSocialValues(nextProps.socialData, question, answers);
      });

      this.setState({ answers });
    }
  }

  getLanguage() {
    return StoreContainer.LanguageStore.getDisplayName() || "English";
  }

  loadState() {
    const answers: any = {};

    this.props.survey.SurveyQuestions.forEach((question: any) => {
      // set the default language answer to English
      if (question.QuestionType === QuestionType.Language) {
        answers[question.QuestionID] = this.getLanguage();
      }

      // if the star rating is already set, use it.
      if (question.QuestionType === QuestionType.StarRating) {
        const currentRating = this.props.agentRating;

        if (currentRating === null) {
          answers[question.QuestionID] = null;
        } else {
          answers[question.QuestionID] = currentRating;
        }
      }

      if (
        !question.VariableName &&
        question.QuestionType === QuestionType.Name
      ) {
        answers[question.QuestionID] = this.props.visitorName;
      }

      if (
        !question.VariableName &&
        question.QuestionType === QuestionType.Email
      ) {
        answers[question.QuestionID] = this.props.visitorEmail;
      }

      if (question.VariableName) {
        const dynamicProperties = this.props.dynamicProperties;

        if (dynamicProperties) {
          const value = dynamicProperties[question.VariableName.trim()];
          if (value) {
            answers[question.QuestionID] = value;
          }
        }
      }

      this.setSocialValues(this.props.socialData, question, answers);
    });

    let totalPages = 1;
    let pagedQuestions: any = {};
    const sectionHash: any = {}; //This lets us know what the current value of every section question in the survey is.
    const sectionParents: any = {}; //A dictionary of sections that have parent sections

    this.props.survey.SurveyQuestions.forEach((question: any, i: any) => {
      if (question.QuestionType === QuestionType.PageBreak) {
        totalPages++;
      } else {
        pagedQuestions[totalPages] = pagedQuestions[totalPages] || [];
        pagedQuestions[totalPages].push(question);
      }

      if (question.QuestionType === QuestionType.Section) {
        sectionHash[question.QuestionID] = 0;

        if (question.SectionID) {
          question.SurveyQuestionChoices.forEach((choice: any) => {
            sectionParents[choice.ChoiceID] = question.SectionID;
          });
        }
      }
    });

    this.sectionHash = sectionHash;
    this.sectionParents = sectionParents;

    // Loop through keys for paged questions and re-set the ids.
    // This eliminates empty pages and false totalPages values
    // if the user created two page breaks right next to each other.

    totalPages = 0;
    const mergedPages: any = {};

    Object.keys(pagedQuestions).forEach((key) => {
      totalPages++;
      mergedPages[totalPages] = pagedQuestions[key];
    });

    pagedQuestions = mergedPages;

    return {
      currentPage: 1,
      totalPages: totalPages,
      answers: answers,
      errors: {},
      pagedQuestions: pagedQuestions,
      submittingSurvey: false,
      visibleSections: [] as any[]
    };
  }

  formatSocialAnswer(socialData: SocialData) {
    if (!socialData) {
      return "";
    }

    return (
      "DisplayName=" +
      socialData.displayName +
      "|Email=" +
      socialData.email +
      "|HeadshotUrl=" +
      socialData.headshotUrl
    );
  }

  setSocialValues(socialData: SocialData, question: any, answers: any) {
    if (question.QuestionType === QuestionType.Name && socialData) {
      answers[question.QuestionID] = socialData.displayName;
    }

    if (question.QuestionType === QuestionType.Email && socialData) {
      answers[question.QuestionID] = socialData.email;
    }

    if (question.QuestionType === QuestionType.Social && socialData) {
      answers[question.QuestionID] = this.formatSocialAnswer(socialData);
    }
  }

  //called every time a section question is answered
  onChangeSection = (questionID: any, newSectionID: any) => {
    const sectionHash = this.sectionHash;

    //Update our section hash with the new choice
    sectionHash[questionID] = newSectionID;

    //Create a list of all currectly active sections that have been chosen from section Questions
    //nulls are allowed in here if a section question hasn't been answered
    const activeSections = Object.keys(sectionHash).map((key) => {
      const sectionID = sectionHash[key];
      if (sectionID) {
        return sectionID;
      }
      return null;
    });

    //An activeSection is only visible if all parent sections are also visible.
    const visibleSections = activeSections.map((activeSection) => {
      if (
        activeSection &&
        this.isSectionVisible(activeSections, activeSection)
      ) {
        return activeSection;
      }

      return null;
    });

    //Set the state with the current visible sections so that we can show/hide appropriate questions
    this.setState({ visibleSections });
  };

  //Called recursively
  isSectionVisible(activeSections: any, sectionID: any): boolean {
    if (activeSections.indexOf(sectionID) === -1) {
      return false;
    }

    if (this.sectionParents.hasOwnProperty(sectionID)) {
      return this.isSectionVisible(
        activeSections,
        this.sectionParents[sectionID]
      );
    }

    return true;
  }

  onPage = (offset: number) => {
    this.setState(
      {
        currentPage: this.state.currentPage + offset
      },
      () => {
        this.el.scrollTop = 0;
      }
    );
  };

  onChange = (question: any, answer: any) => {
    const answers = this.state.answers;

    answers[question.QuestionID] = answer;

    this.setState({
      answers: answers
    });
  };

  onSubmit = () => {
    this.setState({ submittingSurvey: true });
    const survey = this.props.survey;
    const state = this.state;

    const getQuestion = (questionId: any) => {
      const questions = survey.SurveyQuestions.filter((question: any) => {
        return question.QuestionID === questionId;
      });

      return questions[0];
    };

    const answers = [];

    for (const key in state.answers) {
      if (state.answers.hasOwnProperty(key)) {
        const questionID = Number(key);
        const question = getQuestion(questionID);
        if (
          isSectionVisible(question.SectionId, state.visibleSections) === false
        ) {
          continue;
        }

        let answer = state.answers[questionID];

        // normalize answers. the server is expecting a string.
        if (answer === null || answer === undefined) {
          answer = "";
        }

        if (answer.hasOwnProperty("answer")) {
          answer = answer.answer;
        }

        answer = String(answer);

        answers.push({
          Priority: question.Priority,
          QuestionID: questionID,
          Question: question.QuestionText,
          QuestionType: question.QuestionType,
          KBSearchQuestion: question.KBSearchQuestion,
          Answer: answer
        });
      }
    }

    this.props.onSubmit(
      answers.sort((a, b) => {
        return a.Priority - b.Priority;
      })
    );
  };

  onValidationError = (errors: any) => {
    this.setState({
      errors: errors
    });
  };

  focus() {
    this.el.focus();
  }

  render() {
    return (
      <div className="velaro-survey" ref={(el) => (this.el = el)}>
        <div className="velaro-survey-header-text">{this.props.headerText}</div>
        <SurveyForm
          currentPage={this.state.currentPage}
          totalPages={this.state.totalPages}
          visibleSections={this.state.visibleSections}
          submitButtonText={this.props.submitButtonText}
          answers={this.state.answers}
          errors={this.state.errors}
          onChange={this.onChange}
          onChangeSection={this.onChangeSection}
          onSubmit={this.onSubmit}
          onValidationError={this.onValidationError}
          pagedQuestions={this.state.pagedQuestions}
          onPage={this.onPage}
          submittingSurvey={this.state.submittingSurvey}
        />
      </div>
    );
  }
}

export default Survey;
